import React from 'react';
import './Grid.scss';

export type GridProps = {
  alignContent?:
    | 'stretch'
    | 'start'
    | 'end'
    | 'center'
    | 'space-between'
    | 'space-around';
  alignItems?: 'stretch' | 'start' | 'end' | 'center' | 'baseline';
  alignSelf?: 'stretch' | 'start' | 'end' | 'center';
  bleed?: boolean;
  customClassName?: string;
  direction?: 'row' | 'column' | 'row-reverse' | 'column-reverse';
  justifyContent?:
    | 'start'
    | 'end'
    | 'center'
    | 'space-between'
    | 'space-around';
  wrap?: 'no-wrap' | 'wrap' | 'wrap-reverse';
};

type TagProp<C extends React.ElementType> = {
  tag?: C;
};

type GridExtendedProps<
  C extends React.ElementType,
  GridProps,
> = React.PropsWithChildren<GridProps & TagProp<C>> &
  React.ComponentPropsWithoutRef<C>;

/**
 * @name getClassName
 * @function generates a list of classes to be applied based
 * on the props.
 *
 * @param {String} prop - property key
 * @param {String} value - property value
 *
 * @returns {String} classname for key
 */

export function getClassName(
  props: string,
  value: string | boolean | null | undefined
): string | null {
  switch (props) {
    case 'alignContent': {
      // stetch is the default value, there is no corresponding class in reflex-grid
      if (value === 'stretch') return null;
      // not stretch?  apply the correct class
      return `grid--align-content-${value}`;
    }
    case 'alignItems': {
      // stetch is the default value, there is no corresponding class in reflex-grid
      if (value === 'stretch') return null;
      // not stretch?  apply the correct class
      return `grid--align-${value}`;
    }
    case 'alignSelf':
      return value ? `grid--align-self-${value}` : null;
    case 'bleed':
      return value === true ? 'grid--bleed' : null;
    case 'direction':
      return `grid--direction-${value}`;
    case 'justifyContent':
      return `grid--justify-${value}`;
    case 'wrap':
      return `grid--${value}`;
    default:
      return null;
  }
}

const Grid = <C extends React.ElementType = 'section'>({
  alignContent = 'stretch',
  alignItems = 'stretch',
  alignSelf,
  bleed = false,
  children,
  customClassName,
  direction = 'row',
  justifyContent = 'start',
  wrap = 'wrap',
  tag,
}: GridExtendedProps<C, GridProps>) => {
  const Tag = tag || 'section';
  const properties: GridProps = {
    alignContent,
    alignItems,
    alignSelf,
    bleed,
    direction,
    justifyContent,
    wrap,
  };
  // Get the reflex-grid specific classname/decorator;
  const classnames = Object.keys(properties).reduce(
    (previous, current) => {
      const compClass = getClassName(
        current,
        properties[current as keyof GridProps]
      );
      if (compClass !== null) previous.push(compClass);
      return previous;
    },
    ['']
  );

  // join all the decorators together;
  const classes = [...classnames, customClassName].join(' ').trim();

  return (
    <Tag className={`grid ${classes}`} data-grid="grid">
      {children}
    </Tag>
  );
};

export default Grid;

Grid.displayName = 'Grid';
